import { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { User, Search, CalendarPlus } from 'lucide-react';
import FilterBar from '@/components/FilterBar';
import AIAnalysisPanel from '@/components/AIAnalysisPanel';
import IssueIdLink from '@/components/IssueIdLink';
import AddToCalendarModal from '@/components/AddToCalendarModal';
import CalendarIndicator from '@/components/CalendarIndicator';
import useCalendarizedIssues from '@/hooks/useCalendarizedIssues';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function UserView({ token }) {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [filters, setFilters] = useState({ project: '', category: '', user: '', status: '', includeResolved: false });
  const [calendarModalIssue, setCalendarModalIssue] = useState(null);
  
  // Calendar tracking
  const { getCalendarEvents, refresh: refreshCalendar } = useCalendarizedIssues(token);

  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (!loading) {
      fetchFilteredIssues();
    }
  }, [filters]);

  const fetchData = async () => {
    try {
      const analyticsParams = new URLSearchParams();
      if (filters.includeResolved) {
        analyticsParams.append('include_resolved', 'true');
      }
      
      const analyticsResponse = await axios.get(`${BACKEND_URL}/api/analytics/overview?${analyticsParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setAnalytics(analyticsResponse.data);
      
      await fetchFilteredIssues();
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredIssues = async () => {
    try {
      const params = new URLSearchParams({ page_size: 1000 });
      
      // Add includeResolved flag (default is false = stato < 80)
      if (filters.includeResolved) {
        params.append('include_resolved', 'true');
      }
      
      if (filters.status && filters.status !== 'all') {
        params.append('filter_state', filters.status);
      }
      if (filters.project && filters.project !== 'all') {
        params.append('filter_project', filters.project);
      }
      if (filters.category && filters.category !== 'all') {
        params.append('filter_category', filters.category);
      }
      if (filters.user && filters.user !== 'all') {
        params.append('filter_user', filters.user);
      }

      const issuesResponse = await axios.get(`${BACKEND_URL}/api/issues?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      
      setIssues(issuesResponse.data.issues);
      setFilteredIssues(issuesResponse.data.issues);
    } catch (error) {
      toast.error('Errore nel caricamento issues');
      console.error(error);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const analyticsResponse = await axios.get(`${BACKEND_URL}/api/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 90000,
      });
      setAnalytics(analyticsResponse.data);
      
      await fetchFilteredIssues();
      toast.success('Dati aggiornati con successo!');
    } catch (error) {
      toast.error(`Errore nell'aggiornamento: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Calculate user task data from filteredIssues (updates with filters)
  const userTaskData = (() => {
    const userCounts = {};
    (filteredIssues || []).forEach(issue => {
      const userName = issue.handler?.real_name || issue.handler?.name;
      if (userName) {
        userCounts[userName] = (userCounts[userName] || 0) + 1;
      }
    });
    return Object.entries(userCounts)
      .map(([name, count]) => ({ name, tasks: count }))
      .sort((a, b) => b.tasks - a.tasks);
  })();

  const filteredUsers = userTaskData.filter((user) =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getUserIssues = (userName) => {
    return filteredIssues.filter(
      (issue) => (issue.handler?.real_name || issue.handler?.name) === userName
    );
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('resolved') || s.includes('closed')) return 'rgb(16, 185, 129)';
    if (s.includes('assigned') || s.includes('in progress')) return 'rgb(59, 130, 246)';
    if (s.includes('feedback') || s.includes('acknowledged')) return 'rgb(245, 158, 11)';
    return 'rgb(161, 161, 170)';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-spinner">
        <div className="text-[rgb(161,161,170)] font-mono">Caricamento...</div>
      </div>
    );
  }

  const projects = [...new Set((issues || []).map(i => i.project?.name).filter(Boolean))];
  const categories = [...new Set((issues || []).map(i => i.category?.name).filter(Boolean))];
  const users = [...new Set((issues || []).map(i => i.handler?.real_name || i.handler?.name).filter(Boolean))];
  const statuses = [...new Set((issues || []).map(i => i.status?.name).filter(Boolean))];

  return (
    <div className="space-y-6" data-testid="user-view">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-mono mb-2" data-testid="user-view-title">
          Vista Utenti
        </h1>
        <p className="text-[rgb(161,161,170)] text-sm font-mono uppercase tracking-widest">
          Task assignment & workload
        </p>
      </div>

      <FilterBar
        projects={projects}
        categories={categories}
        users={users}
        statuses={statuses}
        onFilterChange={handleFilterChange}
        onRefresh={handleRefresh}
        isRefreshing={refreshing}
        onAIAnalysis={() => setAiPanelOpen(true)}
      />

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[rgb(161,161,170)]" />
        <Input
          type="text"
          placeholder="Cerca utente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-black/20 border-[rgb(39,39,42)] focus:border-[rgb(59,130,246)] focus:ring-1 focus:ring-[rgb(59,130,246)] font-mono"
          data-testid="user-search-input"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Task Chart */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="user-tasks-chart">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Tasks per Utente</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Top performers
            </p>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={filteredUsers.slice(0, 15)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(39, 39, 42)" />
              <XAxis type="number" stroke="rgb(161, 161, 170)" style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
              <YAxis
                dataKey="name"
                type="category"
                stroke="rgb(161, 161, 170)"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '10px' }}
                width={150}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgb(24, 24, 27)',
                  border: '1px solid rgb(39, 39, 42)',
                  borderRadius: '0.375rem',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              />
              <Bar dataKey="tasks" fill="rgb(59, 130, 246)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* User List */}
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="user-list">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Elenco Utenti</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              Click per dettagli
            </p>
          </div>
          <div className="space-y-2 overflow-y-auto" style={{ maxHeight: '400px' }}>
            {filteredUsers.map((user) => (
              <div
                key={user.name}
                onClick={() => setSelectedUser(user.name)}
                className={`
                  p-3 rounded-sm cursor-pointer border transition-all duration-200
                  ${
                    selectedUser === user.name
                      ? 'bg-[rgb(59,130,246)]/10 border-[rgb(59,130,246)]/50'
                      : 'bg-[rgb(39,39,42)]/30 border-[rgb(39,39,42)] hover:border-[rgb(59,130,246)]/50 hover:bg-[rgb(39,39,42)]/50'
                  }
                `}
                data-testid={`user-item-${user.name}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span className="font-mono text-sm">{user.name}</span>
                  </div>
                  <Badge className="rounded-sm px-2 py-0.5 text-xs font-mono bg-[rgb(59,130,246)]">
                    {user.tasks}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* User Issues Detail */}
      {selectedUser && (
        <div className="bg-[rgb(24,24,27)]/50 border border-[rgb(39,39,42)]/50 p-6 rounded-md backdrop-blur-sm" data-testid="user-issues-detail">
          <div className="border-b border-[rgb(39,39,42)]/50 pb-4 mb-4">
            <h3 className="text-xl font-mono font-bold">Tasks di {selectedUser}</h3>
            <p className="text-[rgb(161,161,170)] text-xs uppercase tracking-widest font-mono mt-1">
              {getUserIssues(selectedUser).length} tasks totali
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[rgb(39,39,42)]">
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">ID</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Summary</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Status</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Priority</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)]">Project</TableHead>
                  <TableHead className="font-mono text-xs uppercase tracking-widest text-[rgb(161,161,170)] w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getUserIssues(selectedUser).map((issue) => (
                  <TableRow key={issue.id} className="border-[rgb(39,39,42)] hover:bg-white/5" data-testid={`issue-row-${issue.id}`}>
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-1.5">
                        <IssueIdLink issueId={issue.id} />
                        <CalendarIndicator events={getCalendarEvents(issue.id)} />
                      </div>
                    </TableCell>
                    <TableCell className="max-w-md truncate text-sm">{issue.summary}</TableCell>
                    <TableCell>
                      <Badge
                        className="rounded-sm px-2 py-0.5 text-xs font-mono uppercase tracking-wider"
                        style={{ backgroundColor: getStatusColor(issue.status?.name) }}
                      >
                        {issue.status?.name || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{issue.priority?.label || 'N/A'}</TableCell>
                    <TableCell className="text-sm">{issue.project?.name || 'N/A'}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => setCalendarModalIssue(issue)}
                        className="p-1.5 rounded hover:bg-[rgb(63,63,70)] text-[rgb(161,161,170)] hover:text-[rgb(59,130,246)] transition-colors"
                        title="Aggiungi al calendario"
                      >
                        <CalendarPlus className="h-4 w-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Calendar Modal */}
      {calendarModalIssue && (
        <AddToCalendarModal
          issue={calendarModalIssue}
          token={token}
          onClose={() => setCalendarModalIssue(null)}
          onSuccess={() => {
            toast.success('Evento aggiunto al calendario!');
            refreshCalendar();
          }}
        />
      )}

      <AIAnalysisPanel
        issues={filteredIssues}
        context="Vista Utenti - Analisi del carico di lavoro e distribuzione task per utente"
        token={token}
        isOpen={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
      />
    </div>
  );
}

export default UserView;